import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCardColor, getCardColorOptions } from "../common/colors";

export default function CardContentEditDialog({ open, onClose, onSave, card }) {
  const [tempTitle, setTempTitle] = useState("");
  const [tempSubtitle, setTempSubtitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [selectedColor, setSelectedColor] = useState("default");
  const [titleWidget, setTitleWidget] = useState(null);
  const [saving, setSaving] = useState(false);
  const theme = useTheme();

  const handleCardData = (card) => {
    if (!card) {
      console.warn("Card data is null or undefined");
      return;
    }
    try {
      const tw = card.widgets?.find(
        (w) =>
          w?.skin === "default.title" || w?.skin === "default.automationtitle"
      );

      if (tw?.title) {
        setTempTitle(tw.title);
        setTempSubtitle(tw?.subtitle || "");
        setTitleWidget(tw);
      } else {
        setTempTitle("");
        setTempSubtitle("");
      }

      if (card.setting?.backgroundColor) {
        setSelectedColor(card.setting.backgroundColor);
      } else {
        setSelectedColor("default");
      }
    } catch (error) {
      console.error("Error processing card data:", error);
      setTempTitle("");
      setTempSubtitle("");
      setSelectedColor("default");
    }
  };

  useEffect(() => {
    try {
      if (card) {
        handleCardData(card);
      }
    } catch (error) {
      console.error("Error in useEffect:", error);
    }
  }, [card, open]);

  const handleTitleChange = (e) => {
    setTempTitle(e.target.value);
    setTitleError(!e.target.value.trim());
  };

  const handleSave = async () => {
    if (!tempTitle.trim()) {
      setTitleError(true);
      return;
    }

    setSaving(true);
    try {
      if (onSave) {
        const updates = [];
        if (tempTitle) {
          updates.push({
            ...titleWidget,
            title: tempTitle,
          });
        }

        if (updates.length > 0 || selectedColor) {
          await onSave({
            widget: updates,
            setting: {
              ...(card?.setting || {}),
              backgroundColor: selectedColor,
            },
          });
        }
      }
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { minWidth: { xs: "90%", sm: "400px" } },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>Edit Card Content</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          disabled
          margin="dense"
          label="Title"
          fullWidth
          value={tempTitle}
          onChange={handleTitleChange}
          error={titleError}
          helperText={titleError ? "Title cannot be empty" : ""}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="card-color-label">Card Color</InputLabel>
          <Select
            labelId="card-color-label"
            value={selectedColor}
            label="Card Color"
            onChange={(e) => setSelectedColor(e.target.value)}
          >
            {getCardColorOptions(theme.palette.mode).map((color) => (
              <MenuItem
                key={color.value}
                value={color.value}
                sx={{
                  color: theme.palette.mode === "dark" ? "#fff" : "#222",
                  backgroundColor:
                    color.value === "default"
                      ? theme.palette.background.card
                      : getCardColor(color.value, theme.palette.mode),
                  "&:hover": {
                    backgroundColor:
                      color.value === "default"
                        ? theme.palette.background.card
                        : getCardColor(color.value, theme.palette.mode),
                    opacity: 0.9,
                  },
                }}
              >
                {color.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Box sx={{ position: "relative" }}>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              minWidth: "80px",
            }}
          >
            Save
            {saving && (
              <CircularProgress
                size={20}
                sx={{
                  ml: 1,
                }}
              />
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
